import { useMemo } from "react";

import {
    DEFAULT_COLORS,
    HeatMapContext,
    buildGetColor,
    buildNormalizedScale,
    buildRowGetColor,
    buildRowNormalizedScales,
    classNames,
    type IHeatMapContextValue,
    type IHeatMapProps,
} from "./heatmap.shared";
import { Root } from "./HeatMap.styles";
import { HeatMapGrid } from "./HeatMapGrid";
import { HeatMapLegend } from "./HeatMapLegend";

export type { ColorScale, HeatMapData, HeatMapStyle } from "./heatmap.shared";

const HeatMapRoot = ({
    data,
    colorScale,
    getColor: getColorProp,
    scaleStrategy = "global",
    cellMinWidth = 16,
    cellHeight = 16,
    gap = 4,
    className,
    style,
    children,
}: IHeatMapProps) => {
    const rows = useMemo(() => Object.keys(data), [data]);
    const columns = useMemo(() => {
        const columnKeys = new Set<string>();

        for (const rowKey of Object.keys(data)) {
            for (const columnKey of Object.keys(data[rowKey] ?? {})) {
                columnKeys.add(columnKey);
            }
        }

        return Array.from(columnKeys);
    }, [data]);

    const resolvedScale = useMemo(() => colorScale ?? buildNormalizedScale(DEFAULT_COLORS, data), [colorScale, data]);
    const rowScales = useMemo(
        () => (scaleStrategy === "row" ? buildRowNormalizedScales(DEFAULT_COLORS, data) : null),
        [scaleStrategy, data],
    );
    const getColor = useMemo(() => {
        if (getColorProp) {
            return getColorProp;
        }

        if (rowScales) {
            return buildRowGetColor(DEFAULT_COLORS, rowScales);
        }

        return buildGetColor(resolvedScale);
    }, [getColorProp, rowScales, resolvedScale]);

    const contextValue = useMemo<IHeatMapContextValue>(
        () => ({
            data,
            rows,
            columns,
            getColor,
            colorScale: resolvedScale,
            cellMinWidth,
            cellHeight,
            gap,
        }),
        [data, rows, columns, getColor, resolvedScale, cellMinWidth, cellHeight, gap],
    );

    return (
        <HeatMapContext.Provider value={contextValue}>
            <Root
                $cellMinWidth={cellMinWidth}
                $cellHeight={cellHeight}
                $gap={gap}
                className={classNames("heat-map", className)}
                data-slot="root"
                style={style}
            >
                {children}
            </Root>
        </HeatMapContext.Provider>
    );
};

type IHeatMapCompoundComponent = typeof HeatMapRoot &
    Record<"Grid", typeof HeatMapGrid> &
    Record<"Legend", typeof HeatMapLegend>;

const HeatMap = HeatMapRoot as IHeatMapCompoundComponent;

HeatMap.Grid = HeatMapGrid;
HeatMap.Legend = HeatMapLegend;

export { HeatMap };
